import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaUser, FaClipboardCheck } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig.js';  // Import axiosInstance
import { formatMongoDate } from '../utils/dateUtils.js';  // Import date utilities
import { getActionErrorMessage } from '../utils/errorHandler';
import Navbar from "./CollapsibleNavbar.jsx";
import "../styles/theme.css";
import PerceptorAvailabilityForm from "./PerceptorAvailabilityForm.jsx";
import ViewPreceptorSchedule from "./ViewPreceptorSchedule.jsx";
import PreceptorEvaluationForm from "./PreceptorEvaluationForm.jsx";
import ModifyUser from "./ModifyUser.jsx";
import ImportUsers from './ImportUsers.jsx';
import NonAdminResourceCenter from "./NonAdminResourceCenter.jsx";
import NonAdminReportCenter from "./NonAdminReportCenter.jsx";
import ChangePassword from "./ChangePassword";
import DueEvents from './DueEvents.jsx';
import LoadingSpinner from './LoadingSpinner';
import { normalizeUser } from '../utils/userUtils';
import { isImpersonating } from '../utils/auth';
import ImpersonationBanner from './ImpersonationBanner';
import { formatPreceptorRanks } from '../utils/rankUtils';
import MyEvaluationsPage from './MyEvaluationsPage.jsx';
import StudentAttendancePage from './StudentAttendancePage.jsx';
import MyOrganizationPage from './MyOrganizationPage.jsx';

const PreceptorDashboard = () => {
  const [preceptorData, setPreceptorData] = useState(null);
  const [ranks, setRanks] = useState([]);
  const [error, setError] = useState("");
  const [currentRotation, setCurrentRotation] = useState({
    rotation_start: null,
    rotation_end: null,
    students: []
  });
  const navigate = useNavigate();
  const { view } = useParams();

  // Initialize currentView from URL parameter or default to "dashboard"
  const currentView = view || "dashboard";
  
  const darkPageShellStyle = {
    background: 'transparent',
    minHeight: '100vh'
  };

  const modernContentBoxStyle = {
    border: '1px solid rgba(193, 212, 233, 0.38)',
    borderRadius: '22px',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 248, 253, 0.98) 100%)',
    boxShadow: '0 20px 42px rgba(6, 20, 35, 0.24)'
  };

  // Update URL when view changes
  const handleViewChange = (newView) => {
    if (newView === "dashboard") {
      navigate("/PreceptorDashboard", { replace: true });
    } else {
      navigate(`/PreceptorDashboard/${newView}`, { replace: true });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
        try {
            // Wait for token to be available
            let retries = 0;
            while (!localStorage.getItem('access_token') && retries < 10) {
                await new Promise(resolve => setTimeout(resolve, 100));
                retries++;
            }

            // Verify token
            const token = localStorage.getItem('access_token');
            if (!token) {
                console.error('No token found after waiting');
                throw new Error('No authentication token found');
            }

            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🔑 Token found, making API calls...');

            // Fetch preceptor data
            const preceptorResponse = await axiosInstance.get("/auth/preceptor/dashboard");
            if (!preceptorResponse.data?.data) {
              throw new Error('Invalid preceptor data received');
            }
            setPreceptorData(normalizeUser(preceptorResponse.data.data));

            axiosInstance.get('/ranks/ranks')
                .then((res) => setRanks(Array.isArray(res.data) ? res.data : []))
                .catch((err) => console.error('Failed to load ranks:', err));

            // Fetch students data only; a 404 here means the backend has no current rotation yet.
            try {
              const studentsResponse = await axiosInstance.get("/crud/preceptor/schedule_of_current_rotation");
              setCurrentRotation(studentsResponse.data);
            } catch (scheduleError) {
              if (scheduleError.response?.status === 404) {
                setCurrentRotation({
                  rotation_start: null,
                  rotation_end: null,
                  students: []
                });
              } else {
                throw scheduleError;
              }
            }

        } catch (err) {
            console.error('Fetch Error:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status
            });

            if (err.code === 'ERR_NETWORK') {
                setError('Unable to connect to server. Please verify the backend is running.');
            } else if (err.response?.status === 401) {
                navigate('/');
            } else {
                setError(getActionErrorMessage(err, 'load preceptor dashboard'));
            }
        }
    };

    fetchData();
}, [navigate]);

  const renderDashboardContent = () => {
    const fmtRank = (r, preceptor) => (preceptor ? formatPreceptorRanks(preceptor, ranks) : (r || 'N/A'));
    
    return (
        <div className="modern-dashboard">
            <div className="dashboard-header">
                <h1 className="dashboard-title" style={{ color: '#ffffff' }}>Welcome, Dr. {preceptorData.firstname} {preceptorData.lastname}</h1>
                <p className="dashboard-subtitle" style={{ color: 'rgba(255, 255, 255, 0.92)' }}>Preceptor Dashboard</p>
            </div>

            <div className="dashboard-grid">
                <div className="dashboard-card dashboard-card--dark">
                    <div className="card-header">
                        <FaUser style={{ color: '#7dd3fc' }} />
                        <h2 className="card-title">Personal Information</h2>
                    </div>
                    <div className="card-content">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Preceptor ID</span>
                                <span className="info-value">{preceptorData._id}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Email</span>
                                <span className="info-value">{preceptorData.email}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Institution</span>
                                <span className="info-value">
                                  {(Array.isArray(preceptorData.institutionNames) && preceptorData.institutionNames.length > 0)
                                    ? preceptorData.institutionNames.join(', ')
                                    : (preceptorData.institutionName || '')}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Specialty</span>
                                <span className="info-value">
                                  {(Array.isArray(preceptorData.specialityNames) && preceptorData.specialityNames.length > 0)
                                    ? preceptorData.specialityNames.join(', ')
                                    : (preceptorData.specialityName || '')}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Rank</span>
                                <span className="info-value role-badge">{fmtRank(preceptorData.rank, preceptorData)}</span>
                            </div>
                            {preceptorData.currentCurriculum && (
                                <div className="info-item">
                                    <span className="info-label">Active Curriculum</span>
                                    <span className="info-value curriculum-badge">
                                        {preceptorData.currentCurriculum.name}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="dashboard-card dashboard-card--dark">
                    <div className="card-header">
                        <FaClipboardCheck style={{ color: '#7dd3fc' }} />
                        <h2 className="card-title">Current Students</h2>
                    </div>
                    <div className="card-content">
                        {currentRotation && (currentRotation.rotation_start || currentRotation.rotationStartDate) ? (
                            <>
                                <div className="rotation-period" style={{ background: '#f8fafc', borderLeft: '4px solid #10b981' }}>
                                    <span className="period-label" style={{ color: '#64748b' }}>Rotation Period:</span>
                                    <span className="period-dates" style={{ color: '#1e293b' }}>
                                        {formatMongoDate(currentRotation.rotationStartDate || currentRotation.rotation_start)} 
                                        — {formatMongoDate(currentRotation.rotationEndDate || currentRotation.rotation_end)}
                                    </span>
                                </div>
                                {currentRotation.students?.length > 0 ? (
                                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                                        {currentRotation.students.map((student) => (
                                            <div key={student.id} style={{ 
                                                background: '#f8fafc', 
                                                borderRadius: '10px', 
                                                padding: '0.75rem', 
                                                border: '1px solid #e2e8f0',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>{student.name}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{student.email}</div>
                                                </div>
                                                <span className="role-badge" style={{ fontSize: '0.7rem' }}>{fmtRank(student.rank)}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-data-message" style={{ background: '#f8fafc' }}>
                                        <div className="no-data-icon">👥</div>
                                        <p style={{ color: '#64748b' }}>No students currently assigned</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-data-message" style={{ background: '#f8fafc' }}>
                                <div className="no-data-icon">📅</div>
                                <p style={{ color: '#64748b' }}>No active rotation period found</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            <DueEvents userId={preceptorData._id} userRole="preceptor" />
        </div>
    );
  };

  const renderContent = () => {
    switch (currentView) {
      case "choices":
        return <PerceptorAvailabilityForm />;
      case "schedule":
        return <ViewPreceptorSchedule />;
      case "forms":
        return <PreceptorEvaluationForm />;
      case "my-evaluations":
        return <MyEvaluationsPage userLabel="My Evaluations" />;
      case "student-attendance":
        return <StudentAttendancePage adminMode={Boolean(preceptorData?.isAdmin)} />;
      case "evaluation-center":
        return <MyEvaluationsPage userLabel="My Evaluations" />;
      case "my-organization":
        return <MyOrganizationPage canEdit={Boolean(preceptorData?.isAdmin)} />;
      case "my-account":
        return <ModifyUser userId={preceptorData?._id} userType="preceptor" isAdmin={preceptorData?.isAdmin || false} />;
      case "import-students":
        return <ImportUsers initialMode="students" />;
      case "import-users":
        return <ImportUsers />;
      case "resource-center":
        return <NonAdminResourceCenter userType="preceptor" userData={preceptorData} />;
      case "report-center":
        return <NonAdminReportCenter userType="preceptor" userData={preceptorData} />;
      case "change-password":
        return <ChangePassword />;
      case "dashboard":
      default:
        return renderDashboardContent();
    }
  };

  if (error) {
    return (
      <div className="page-container" style={darkPageShellStyle}>
        <div className="content-box" style={modernContentBoxStyle}>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <p className="error-message">{error}</p>
        </div>
        </div>
      </div>
    );
  }

  if (!preceptorData) {
    return (
      <div className="page-container" style={darkPageShellStyle}>
        <div className="content-box" style={modernContentBoxStyle}>
        <LoadingSpinner text="Loading preceptor dashboard..." variant="page" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={darkPageShellStyle}>
      <Navbar
        setCurrentView={handleViewChange}
        userType="preceptor"
        isAdmin={Boolean(preceptorData?.isAdmin)}
      />
      {isImpersonating() && <ImpersonationBanner />}
      {renderContent()}
    </div>
  );
};

export default PreceptorDashboard;
